/**
 * Helpers for saving backup archives straight to a folder on the user's PC
 * using the File System Access API.
 *
 * The browser builds nothing itself — the archive is created on the server,
 * streamed to the browser and then written to the chosen local folder. The
 * directory handle is kept in IndexedDB so it survives page reloads, but the
 * browser may still ask the user to re-grant write permission, which is why
 * every write performs a permission check first.
 *
 * This only works in Chromium-based browsers (Chrome/Edge) and is a manual,
 * foreground operation: automatic/scheduled backups cannot target the PC
 * because they run on the server with no browser open.
 */

const DB_NAME = 'firstaid-os';
const DB_VERSION = 1;
const STORE_NAME = 'backup-local-folders';
const HANDLE_KEY = 'local-backup-folder';

/** Minimal shape of the File System Access API surface we rely on. */
export interface LocalBackupFolderHandle {
  readonly name: string;
  queryPermission(
    descriptor: { mode: 'read' | 'readwrite' },
  ): Promise<PermissionState>;
  requestPermission(
    descriptor: { mode: 'read' | 'readwrite' },
  ): Promise<PermissionState>;
  getFileHandle(
    name: string,
    options?: { create?: boolean },
  ): Promise<{
    createWritable(): Promise<{
      write(data: Blob): Promise<void>;
      close(): Promise<void>;
      abort?(): Promise<void>;
    }>;
  }>;
}

interface DirectoryPickerWindow {
  showDirectoryPicker?: (options?: {
    mode?: 'read' | 'readwrite';
  }) => Promise<LocalBackupFolderHandle>;
}

export interface LocalBackupFolder {
  name: string;
  handle: LocalBackupFolderHandle;
}

/** Whether this browser can write to a folder on the user's PC. */
export function isLocalFolderSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof (window as unknown as DirectoryPickerWindow).showDirectoryPicker ===
      'function' &&
    typeof indexedDB !== 'undefined'
  );
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function runRequest<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest,
): Promise<T> {
  return openDatabase().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, mode);
        const request = run(tx.objectStore(STORE_NAME));
        request.onsuccess = () => resolve(request.result as T);
        request.onerror = () => reject(request.error);
        tx.oncomplete = () => db.close();
        tx.onabort = () => db.close();
      }),
  );
}

/**
 * Opens the OS folder picker and remembers the chosen directory for later
 * backup writes. Resolves with `null` when the user cancels.
 */
export async function pickLocalBackupFolder(): Promise<LocalBackupFolder | null> {
  const picker = (window as unknown as DirectoryPickerWindow)
    .showDirectoryPicker;
  if (!picker) {
    throw new Error('Selecting a folder is not supported in this browser.');
  }
  const handle = await picker({ mode: 'readwrite' });
  await runRequest<unknown>('readwrite', (store) =>
    store.put(handle, HANDLE_KEY),
  );
  return { name: handle.name, handle };
}

/** Returns the previously chosen folder, if any. Never prompts. */
export async function getLocalBackupFolder(): Promise<LocalBackupFolder | null> {
  if (typeof indexedDB === 'undefined') return null;
  try {
    const handle = await runRequest<LocalBackupFolderHandle | undefined>(
      'readonly',
      (store) => store.get(HANDLE_KEY),
    );
    if (!handle || typeof handle.getFileHandle !== 'function') return null;
    return { name: handle.name, handle };
  } catch {
    return null;
  }
}

/** Forgets the chosen folder. */
export async function clearLocalBackupFolder(): Promise<void> {
  try {
    await runRequest<unknown>('readwrite', (store) => store.delete(HANDLE_KEY));
  } catch {
    /* ignore */
  }
}

/** Makes sure the folder is writable, prompting the user when required. */
async function ensureWritePermission(
  handle: LocalBackupFolderHandle,
): Promise<boolean> {
  const descriptor = { mode: 'readwrite' as const };
  try {
    if ((await handle.queryPermission(descriptor)) === 'granted') return true;
    return (await handle.requestPermission(descriptor)) === 'granted';
  } catch {
    return false;
  }
}

/**
 * Returns the remembered folder after making sure it is writable, prompting
 * the user for permission when needed. Call this directly from a click handler
 * so the permission request still has user activation — then fetch and write
 * the (potentially large) archive afterwards.
 */
export async function ensureLocalBackupFolderAccess(): Promise<LocalBackupFolder> {
  const folder = await getLocalBackupFolder();
  if (!folder) {
    throw new Error('No folder on this PC has been selected yet.');
  }
  if (!(await ensureWritePermission(folder.handle))) {
    throw new Error(
      `Write access to "${folder.name}" was denied. Select the folder again.`,
    );
  }
  return folder;
}

/** Writes an archive into an already-permitted PC folder. */
export async function writeBackupToLocalFolder(
  folder: LocalBackupFolder,
  blob: Blob,
  filename: string,
): Promise<string> {
  const fileHandle = await folder.handle.getFileHandle(filename, {
    create: true,
  });
  const writable = await fileHandle.createWritable();
  try {
    await writable.write(blob);
    await writable.close();
  } catch (err) {
    try {
      await writable.abort?.();
    } catch {
      /* ignore */
    }
    throw err;
  }
  return filename;
}

/**
 * Convenience helper that ensures access then writes in one step. Prefer the
 * split `ensureLocalBackupFolderAccess` + `writeBackupToLocalFolder` when the
 * blob still has to be downloaded, so permission is granted before the wait.
 */
export async function saveBackupToLocalFolder(
  blob: Blob,
  filename: string,
): Promise<string> {
  const folder = await ensureLocalBackupFolderAccess();
  return writeBackupToLocalFolder(folder, blob, filename);
}
